import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../lib/api';
import { Avatar, Icon, IconName } from './ui';

type Page =
  | 'dashboard'
  | 'generate'
  | 'library'
  | 'audiobooks'
  | 'podcasts'
  | 'settings'
  | 'billing'
  | 'admin'
  | 'admin/users'
  | 'admin/credentials'
  | 'admin/tiers';

interface LayoutProps {
  children: React.ReactNode;
  currentPage: string;
  onNavigate: (page: Page) => void;
}

const navItems: Array<{ id: Page; label: string; icon: IconName }> = [
  { id: 'dashboard', label: 'Dashboard', icon: 'dashboard' },
  { id: 'generate', label: 'Generate', icon: 'generate' },
  { id: 'library', label: 'Library', icon: 'library' },
  { id: 'audiobooks', label: 'Audiobooks', icon: 'book' },
  { id: 'podcasts', label: 'Podcasts', icon: 'mic' },
  { id: 'billing', label: 'Billing', icon: 'billing' },
  { id: 'settings', label: 'Settings', icon: 'settings' },
];

const adminNavItems: Array<{ id: Page; label: string; icon: IconName }> = [
  { id: 'admin', label: 'Overview', icon: 'admin' },
  { id: 'admin/users', label: 'Users', icon: 'users' },
  { id: 'admin/credentials', label: 'Credentials', icon: 'key' },
  { id: 'admin/tiers', label: 'Tiers', icon: 'tiers' },
];

const PAGE_TITLES: Record<string, string> = {
  dashboard: 'Dashboard',
  generate: 'Generate Audio',
  library: 'Audio Library',
  audiobooks: 'Audiobooks',
  podcasts: 'Podcasts',
  billing: 'Billing & Plans',
  settings: 'Settings',
  admin: 'Admin Console',
  'admin/users': 'Admin · Users',
  'admin/credentials': 'Admin · Credentials',
  'admin/tiers': 'Admin · Tiers',
};

function NavRow({
  item,
  active,
  onClick,
}: {
  item: { label: string; icon: IconName };
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-[10px] text-sm font-semibold transition-all duration-150 ${
        active
          ? 'grad-accent text-ink-950 shadow-[0_6px_18px_rgba(var(--a-rgb),.28)]'
          : 'text-mist-400 hover:bg-ink-800 hover:text-mist-200'
      }`}
    >
      <Icon name={item.icon} />
      <span>{item.label}</span>
    </button>
  );
}

function PlanCard({ onNavigate }: { onNavigate: (page: Page) => void }) {
  const { user } = useAuth();
  const [usage, setUsage] = useState<{ used: number; limit: number } | null>(null);

  useEffect(() => {
    let cancelled = false;
    api.getUsage().then((res) => {
      if (!cancelled && res.success && res.data) {
        setUsage({ used: res.data.characters.used, limit: res.data.characters.limit });
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const pct = usage && usage.limit > 0 ? Math.min(100, Math.round((usage.used / usage.limit) * 100)) : 0;
  const fmt = (n: number) => (n >= 1000 ? `${Math.round(n / 1000)}K` : `${n}`);

  return (
    <div
      className="p-4 rounded-[14px] border border-ink-700 mb-3.5"
      style={{ background: 'linear-gradient(180deg, rgba(var(--a-rgb), .1), rgba(19,19,22,0))' }}
    >
      <p className="m-0 mb-1 font-display font-extrabold text-[13px] tracking-[0.04em] uppercase" style={{ color: 'var(--a-hi)' }}>
        {user?.tier.name || 'Free'} plan
      </p>
      <p className="m-0 mb-3 text-mist-400 text-[12.5px] leading-normal">
        {usage ? `${fmt(usage.used)} / ${fmt(usage.limit)} characters used` : 'Loading usage…'}
      </p>
      <div className="h-1.5 rounded-full bg-ink-700 overflow-hidden">
        <div className="h-full rounded-full grad-bar" style={{ width: `${pct}%` }} />
      </div>
      <button onClick={() => onNavigate('billing')} className="btn-accent-outline w-full mt-3.5">
        Upgrade
      </button>
    </div>
  );
}

const VERIFY_BANNER_DISMISSED_KEY = 'verify_email_banner_dismissed';

function VerifyEmailBanner() {
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem(VERIFY_BANNER_DISMISSED_KEY) === '1'
  );
  const [resendState, setResendState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');

  if (!user || user.email_verified || dismissed) return null;

  const handleResend = async () => {
    if (resendState === 'sending') return;
    setResendState('sending');
    const result = await api.resendVerificationEmail();
    setResendState(result.success ? 'sent' : 'error');
  };

  const dismiss = () => {
    sessionStorage.setItem(VERIFY_BANNER_DISMISSED_KEY, '1');
    setDismissed(true);
  };

  return (
    <div
      className="flex items-center gap-3 flex-wrap mx-5 lg:mx-[34px] mt-5 px-4 py-3 rounded-xl text-[13px]"
      style={{
        background: 'rgba(224, 162, 31, .1)',
        border: '1px solid rgba(224, 162, 31, .35)',
        color: '#E8C377',
      }}
    >
      <span className="font-semibold">Verify your email</span>
      <span className="text-mist-300">
        {resendState === 'sent'
          ? `Verification email sent to ${user.email} — check your inbox.`
          : resendState === 'error'
            ? "Couldn't send the email right now. Try again in a moment."
            : `We sent a link to ${user.email}. Click it to verify your account.`}
      </span>
      <span className="ml-auto flex items-center gap-3">
        {resendState !== 'sent' && (
          <button
            onClick={handleResend}
            disabled={resendState === 'sending'}
            className="font-semibold bg-transparent border-0 p-0 cursor-pointer disabled:opacity-50"
            style={{ color: '#E8C377', textDecoration: 'underline' }}
          >
            {resendState === 'sending' ? 'Sending…' : 'Resend email'}
          </button>
        )}
        <button
          onClick={dismiss}
          aria-label="Dismiss verification reminder"
          title="Dismiss"
          className="bg-transparent border-0 p-0 cursor-pointer text-mist-500 hover:text-mist-300 text-[16px] leading-none"
        >
          ×
        </button>
      </span>
    </div>
  );
}

export function Layout({ children, currentPage, onNavigate }: LayoutProps) {
  const { user, logout } = useAuth();
  const isAdmin = user?.role === 'admin';

  const initials =
    (user?.first_name?.[0] || user?.email[0] || '?').toUpperCase() + (user?.last_name?.[0]?.toUpperCase() || '');
  const displayName = user?.first_name ? `${user.first_name} ${user.last_name || ''}`.trim() : user?.email;

  const mobileNav: Array<{ id: Page; label: string; icon: IconName }> = isAdmin
    ? [...navItems, { id: 'admin', label: 'Admin', icon: 'admin' }]
    : navItems;

  return (
    <div
      className="min-h-screen flex text-mist-200"
      style={{
        background:
          'radial-gradient(120% 80% at 82% -10%, rgba(var(--a-rgb),.14) 0%, rgba(12,12,14,0) 46%), radial-gradient(90% 70% at 0% 100%, rgba(31,143,224,.07) 0%, rgba(12,12,14,0) 50%), #0C0C0E',
      }}
    >
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-[250px] flex-none bg-ink-900 border-r border-ink-800 flex-col px-4 py-[22px] sticky top-0 h-screen overflow-y-auto">
        <div className="flex items-center gap-2.5 px-2 pb-6">
          <span className="w-3 h-3 rounded-full grad-accent-simple" />
          <span className="font-display font-extrabold tracking-[0.15em] text-white text-base">AUDIARO</span>
        </div>

        <nav className="flex flex-col gap-1">
          {navItems.map((item) => (
            <NavRow key={item.id} item={item} active={currentPage === item.id} onClick={() => onNavigate(item.id)} />
          ))}
          {isAdmin && (
            <>
              <p className="px-3 mt-4 mb-1 text-[11px] font-semibold text-mist-500 uppercase tracking-[0.14em]">Admin</p>
              {adminNavItems.map((item) => (
                <NavRow key={item.id} item={item} active={currentPage === item.id} onClick={() => onNavigate(item.id)} />
              ))}
            </>
          )}
        </nav>

        <div className="flex-1" />

        <PlanCard onNavigate={onNavigate} />

        <div className="flex items-center gap-[11px] p-2">
          <Avatar text={initials} size={36} className="border-2 border-[var(--a-lo)]" />
          <div className="flex-1 min-w-0">
            <p className="m-0 text-white text-[13.5px] font-semibold truncate">{displayName}</p>
            <p className="m-0 text-mist-500 text-xs">{user?.tier.name} member</p>
          </div>
          <button onClick={logout} className="text-mist-500 hover:text-mist-200 flex transition-colors" title="Log out">
            <Icon name="logout" />
          </button>
        </div>
      </aside>

      {/* Main column */}
      <main className="flex-1 min-w-0 flex flex-col">
        {/* Mobile top bar */}
        <div className="lg:hidden flex items-center justify-between px-5 pt-4 pb-2">
          <div className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full grad-accent-simple" />
            <span className="font-display font-extrabold tracking-[0.14em] text-white text-sm">AUDIARO</span>
          </div>
          <button onClick={() => onNavigate('settings')}>
            <Avatar text={initials} size={34} />
          </button>
        </div>

        {/* Header */}
        <header className="flex items-center justify-between gap-4 px-5 lg:px-[34px] py-4 lg:py-5 border-b border-ink-800 sticky top-0 z-10 backdrop-blur-[10px]" style={{ background: 'rgba(12,12,14,.82)' }}>
          <h1 className="font-display font-extrabold text-white text-lg lg:text-[22px] m-0 tracking-[-0.01em] truncate">
            {PAGE_TITLES[currentPage] || 'Audiaro'}
          </h1>
          <button onClick={() => onNavigate('generate')} className="btn-grad flex-none !py-2.5 !px-[18px] !text-xs !tracking-[0.05em]">
            <Icon name="plus" size={16} />
            New Audio
          </button>
        </header>

        <VerifyEmailBanner />

        <div className="flex-1 px-5 lg:px-[34px] py-6 lg:py-[30px] pb-28 lg:pb-[30px] overflow-y-auto">{children}</div>
      </main>

      {/* Mobile bottom nav */}
      <div
        className="lg:hidden fixed bottom-0 left-0 right-0 flex justify-around px-2 pt-3 pb-[22px] border-t border-ink-800 backdrop-blur-[10px] z-20"
        style={{ background: 'rgba(19,19,22,.94)' }}
      >
        {mobileNav.map((item) => {
          const active = item.id === 'admin' ? currentPage.startsWith('admin') : currentPage === item.id;
          return (
            <button
              key={item.id}
              onClick={() => onNavigate(item.id)}
              title={item.label}
              className={`w-[46px] h-10 rounded-xl flex items-center justify-center transition-all ${
                active ? 'grad-accent-simple text-ink-950' : 'text-mist-500'
              }`}
            >
              <Icon name={item.icon} />
            </button>
          );
        })}
      </div>
    </div>
  );
}
